/**
 * Calendar OAuth result screen driven by the success query parameter. Shows the reported outcome
 * and returns to Settings after three seconds or a button click; does not independently verify
 * connection status.
 *
 * @module apps/web/src/pages/CalendarConnected
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { usePrerenderRoute } from '@/components/layout/KeepAliveRoutes';

/**
 * Calendar OAuth result screen driven by the success query parameter. Shows the reported outcome
 * and returns to Settings after three seconds or a button click; does not independently verify
 * connection status.
 */
export function CalendarConnected() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === 'true';
  const prerender = usePrerenderRoute();

  // Every path from here leads to Settings; render it hidden now so going there only reveals it.
  useEffect(() => prerender('/settings'), [prerender]);

  useEffect(() => {
    // Redirect after 3 seconds
    const timeout = setTimeout(() => {
      navigate('/settings');
    }, 3000);

    return () => clearTimeout(timeout);
  }, [navigate]);

  return (
    <div className="flex h-dvh items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {success ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Calendar Connected
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-red-600" />
                Connection Failed
              </>
            )}
          </CardTitle>
          <CardDescription>
            {success
              ? 'Your Google Calendar has been successfully connected!'
              : 'Failed to connect Google Calendar. Please try again.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm">
            {success
              ? 'Your calendar events will now appear as immovable constraints in your schedule. You can manage calendar settings in the Settings page.'
              : 'There was an error connecting your calendar. Please check your credentials and try again.'}
          </p>
          <Button
            onClick={() => navigate('/settings')}
            className="w-full"
            variant={success ? 'default' : 'destructive'}
          >
            Go to Settings
          </Button>
          <p className="text-muted-foreground text-center text-xs">Redirecting in 3 seconds...</p>
        </CardContent>
      </Card>
    </div>
  );
}
